import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, money, dateFmt, formatApiError, API_BASE } from "@/lib/api";
import { StatusBadge, UrgencyBadge } from "@/components/StatusBadge";
import { STATUS_LABELS } from "@/lib/constants";
import { ArrowLeft, FileText, MapPin, Truck, User, Phone, Calendar, Activity, Save, Download, FileDown, Hospital, BellRing, Heart, MessageCircle, Receipt } from "lucide-react";
import { toast } from "sonner";
import ServiceMap from "@/components/ServiceMap";
import ServiceEvidences from "@/components/ServiceEvidences";
import PreAlertModal from "@/components/PreAlertModal";
import ServiceChargesSheet from "@/components/ServiceChargesSheet";
import HelpTip from "@/components/HelpTip";

export default function ServiceDetail() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [units, setUnits] = useState([]);
    const [personnel, setPersonnel] = useState([]);
    const [newStatus, setNewStatus] = useState("");
    const [comment, setComment] = useState("");
    const [assignUnit, setAssignUnit] = useState("");
    const [chargesOpen, setChargesOpen] = useState(false);
    const [assignParamedic, setAssignParamedic] = useState("");
    const [assignDriver, setAssignDriver] = useState("");
    const [prealertOpen, setPrealertOpen] = useState(false);
    const [sendingNps, setSendingNps] = useState(false);
    const [notifying, setNotifying] = useState(false);
    const [editLogistics, setEditLogistics] = useState(false);
    const [logForm, setLogForm] = useState({});

    const load = async () => {
        try {
            const { data } = await api.get(`/services/${id}`);
            if (!data) return;
            setData(data);
            setAssignUnit(data.service?.unit_id || "");
            setAssignParamedic(data.service?.paramedic_id || "");
            setAssignDriver(data.service?.driver_id || "");
        } catch (e) { console.warn(e); }
    };

    useEffect(() => {
        load();
        (async () => {
            try {
                const [u, p] = await Promise.all([api.get("/units"), api.get("/personnel")]);
                if (u?.data) setUnits(u.data);
                if (p?.data) setPersonnel(p.data);
            } catch (e) { console.warn(e); }
        })();
    }, [id]);

    if (!data) return <div className="text-sm text-slate-500 font-mono">Cargando servicio...</div>;
    const { service, client, unit, paramedic, driver, frap } = data;

    const updateStatus = async () => {
        if (!newStatus) return;
        try {
            await api.patch(`/services/${id}/status`, { status: newStatus, comment });
            toast.success("Estatus actualizado");
            setNewStatus("");
            setComment("");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const openEditLogistics = () => {
        if (!service) return;
        setLogForm({
            origin_address: service.origin_address || "",
            destination_address: service.destination_address || "",
            hospital_destination: service.hospital_destination || "",
            patient_name: service.patient_name || "",
            patient_phone: service.patient_phone || "",
            reason: service.reason || "",
            no_transport: !!service.no_transport,
        });
        setEditLogistics(true);
    };

    const saveLogistics = async () => {
        try {
            await api.patch(`/services/${id}/logistics`, {
                origin: logForm.origin_address || null,
                destination: logForm.destination_address || null,
                hospital_destination: logForm.hospital_destination || null,
                patient_name: logForm.patient_name,
                patient_phone: logForm.patient_phone,
                reason: logForm.reason,
                no_transport: logForm.no_transport,
            });
            toast.success("Logística actualizada");
            setEditLogistics(false);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const notifyClient = async () => {        setNotifying(true);
        try {
            const r = await api.post(`/services/${id}/notify`, { channels: ["whatsapp", "email"] });
            const results = r.data.results || {};
            const sent = Object.keys(results).filter(k => !results[k].mocked && results[k].ok !== false);
            const mocked = Object.keys(results).filter(k => results[k].mocked);
            if (sent.length) toast.success(`Notificación enviada: ${sent.join(", ")}`);
            if (mocked.length) toast.info(`MOCK ${mocked.join(", ")} — configura Twilio/Resend en Integraciones para envío real`);
            if (!sent.length && !mocked.length) toast.warning("No se envió a ningún canal (revisa teléfono/email del cliente)");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setNotifying(false);
        }
    };

    const assignResources = async () => {        try {
            const { data } = await api.patch(`/services/${id}/assign`, {
                unit_id: assignUnit || null,
                paramedic_id: assignParamedic || null,
                driver_id: assignDriver || null,
            });
            toast.success("Recursos asignados");
            // Mostrar advertencias por personal sin check-in del día
            (data?.warnings || []).forEach((w) => toast.warning(w, { duration: 6000 }));
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const createInvoice = async () => {
        try {
            const today = new Date().toISOString().slice(0, 10);
            const due = new Date(Date.now() + (client?.credit_days || 30) * 86400000).toISOString().slice(0, 10);
            await api.post("/invoices", {
                service_id: service.id,
                client_id: client?.id,
                subtotal: service.subtotal || service.base_price,
                iva: service.iva || (service.base_price * 0.16),
                total: service.total,
                issue_date: today,
                due_date: due,
            });
            toast.success("Factura generada");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const sendNPS = async () => {
        setSendingNps(true);
        try {
            const { data } = await api.post(`/services/${id}/nps/send`, {});
            if (data.send_result?.mocked) {
                toast.warning("Encuesta registrada en modo simulado. Configura Resend para envío real.");
            } else {
                toast.success(`Encuesta NPS enviada a ${data.to_email}`);
            }
            // Copiar link al clipboard como conveniencia
            if (data.link && navigator.clipboard) {
                navigator.clipboard.writeText(data.link).catch(() => {});
                toast.message("Link copiado al portapapeles");
            }
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSendingNps(false);
        }
    };

    return (
        <div className="space-y-4" data-testid="service-detail">
            <Link to="/services" className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" /> Servicios
            </Link>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Main */}
                <div className="lg:col-span-2 space-y-4">
                    <div className="fm-card p-5">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <div className="font-mono text-xs text-slate-500">{service.folio}</div>
                                <h2 className="font-display font-black text-2xl tracking-tight">{service.service_type_name}</h2>
                                <div className="flex items-center gap-2 mt-2">
                                    <StatusBadge status={service.status} />
                                    <UrgencyBadge urgency={service.urgency_level} />
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Total</div>
                                <div className="font-mono font-bold text-2xl tabular-nums">{money(service.total)}</div>
                                {service.extras?.length > 0 && (
                                    <div className="text-[10px] text-emerald-700 font-mono">+{service.extras.length} cargo(s) extra</div>
                                )}
                                <button
                                    type="button"
                                    onClick={() => setChargesOpen(true)}
                                    className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2 py-1 border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 rounded-sm"
                                    data-testid="open-charges-btn"
                                    title="Agrega km foráneo, espera, oxígeno, casetas y demás cargos al servicio"
                                >
                                    <Receipt className="w-3 h-3" /> Agregar cargos
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 pt-4 border-t border-slate-200">
                            <div>
                                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1"><User className="w-3 h-3" /> Paciente</div>
                                <div className="text-sm font-medium">{service.patient_name}</div>
                                <div className="text-xs text-slate-500">{service.patient_age ? `${service.patient_age} años` : ""} {service.patient_sex}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Cliente</div>
                                <div className="text-sm font-medium">{client?.business_name || "Particular"}</div>
                                <div className="text-xs text-slate-500">{service.channel}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> Origen</div>
                                <div className="text-sm">{service.origin_address}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> Destino</div>
                                <div className="text-sm">{service.destination_address || "—"}</div>
                            </div>
                            <div className="col-span-2">
                                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Motivo</div>
                                <div className="text-sm">{service.reason || "—"}</div>
                            </div>
                            {service.no_transport && (
                                <div className="col-span-2 bg-amber-50 border-l-4 border-l-amber-400 px-3 py-2 text-xs text-amber-900 rounded-sm">
                                    🩹 <strong>Atendido en el lugar sin traslado</strong>
                                </div>
                            )}
                        </div>
                        <div className="mt-3 pt-3 border-t border-slate-200 flex justify-end">
                            <button onClick={openEditLogistics} className="fm-btn-outline text-xs" data-testid="edit-logistics-btn">
                                ✏️ Editar logística (cambiar destino, marcar sin traslado, etc.)
                            </button>
                        </div>
                    </div>

                    {/* Mapa del trayecto */}
                    <ServiceMap service={service} unit={unit} height={340} />

                    {/* Evidencias del servicio */}
                    <ServiceEvidences service={service} onChange={load} />

                    {/* Pre-avisos enviados al hospital */}
                    {(service.prealerts || []).length > 0 && (
                        <div className="fm-card overflow-hidden" data-testid="prealerts-history">
                            <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                                <h3 className="font-display font-bold text-base flex items-center gap-2">
                                    <BellRing className="w-4 h-4 text-red-600" /> Pre-avisos al hospital
                                </h3>
                                <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500 font-mono">
                                    {service.prealerts.length} aviso(s)
                                </span>
                            </div>
                            <div className="divide-y divide-slate-100">
                                {service.prealerts.slice().reverse().map((pa) => (
                                    <div key={pa.id} className="p-4 text-sm">
                                        <div className="flex items-center justify-between flex-wrap gap-2">
                                            <div className="font-mono text-xs">
                                                {dateFmt(pa.at)} · ETA {pa.eta_minutes || "—"} min · por {pa.by_name}
                                            </div>
                                            {pa.mocked
                                                ? <span className="fm-badge fm-badge-warning">SIMULADO</span>
                                                : <span className="fm-badge fm-badge-positive">ENVIADO</span>}
                                        </div>
                                        <div className="mt-1 text-xs text-slate-600">
                                            Canales: <b>{pa.channels_used.join(", ")}</b>
                                            {" · "}Destinatarios: {(pa.recipients?.emails || []).length} email · {(pa.recipients?.phones || []).length} SMS · {(pa.recipients?.whatsapps || []).length} WA
                                        </div>
                                        {pa.custom_message && <div className="mt-1 text-xs italic">"{pa.custom_message}"</div>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Status timeline */}
                    <div className="fm-card p-5">
                        <h3 className="font-display font-bold text-base mb-3 flex items-center gap-2">
                            <Activity className="w-4 h-4" strokeWidth={1.5} /> Historial de estatus
                        </h3>
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                            {(service.status_history || []).slice().reverse().map((h, i) => (
                                <div key={i} className="flex gap-3 pb-2 border-b border-slate-100 last:border-0">
                                    <div className="w-2 h-2 rounded-full bg-slate-900 mt-1.5 flex-shrink-0"></div>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <StatusBadge status={h.status} />
                                            <span className="text-[10px] font-mono text-slate-500">{dateFmt(h.at)}</span>
                                        </div>
                                        <div className="text-xs text-slate-600 mt-0.5">por {h.by_name || "Sistema"}</div>
                                        {h.comment && <div className="text-xs text-slate-700 mt-0.5">"{h.comment}"</div>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Sidebar actions */}
                <div className="space-y-4">
                    {/* Assignment */}
                    <div className="fm-card p-5">
                        <h3 className="font-display font-bold text-base mb-3 flex items-center gap-2">
                            <Truck className="w-4 h-4" strokeWidth={1.5} /> Asignación
                            <HelpTip text="Asigna ambulancia, paramédico y operador al servicio. El sistema valida que el personal tenga check-in de asistencia hoy. Puedes asignar solo paramédico sin unidad si todavía estás coordinando." />
                        </h3>
                        <div className="space-y-3">
                            <div>
                                <label className="fm-label" title="Lista de ambulancias disponibles. Las que dicen 'ocupada' ya están en otro servicio.">Unidad</label>
                                <select value={assignUnit} onChange={(e) => setAssignUnit(e.target.value)} className="fm-input" data-testid="assign-unit-select" title="Lista de ambulancias. Solo asigna unidades disponibles para no romper otro servicio en curso.">
                                    <option value="">Sin asignar</option>
                                    {units.map((u) => (
                                        <option key={u.id} value={u.id}>{u.economic_number} · {u.ambulance_type} · {u.status}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="fm-label" title="Paramédico responsable del servicio. Será quien firme el FRAP.">Paramédico</label>
                                <select value={assignParamedic} onChange={(e) => setAssignParamedic(e.target.value)} className="fm-input" title="Paramédico responsable. Solo aparecen los que tienen vigencia profesional y check-in hoy.">
                                    <option value="">Sin asignar</option>
                                    {personnel.filter((p) => p.position === "paramedico").map((p) => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="fm-label" title="Operador o chofer de la ambulancia.">Operador / Chofer</label>
                                <select value={assignDriver} onChange={(e) => setAssignDriver(e.target.value)} className="fm-input" title="Operador o chofer de la ambulancia. Su check-in también se valida.">
                                    <option value="">Sin asignar</option>
                                    {personnel.filter((p) => p.position === "operador").map((p) => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                </select>
                            </div>
                            <button onClick={assignResources} className="fm-btn-primary w-full justify-center" data-testid="assign-btn" title="Guarda la asignación. La tripulación verá el servicio en su CrewMobile en segundos.">
                                <Save className="w-3.5 h-3.5" /> Asignar
                            </button>
                        </div>
                    </div>

                    {/* Update status */}
                    <div className="fm-card p-5">
                        <h3 className="font-display font-bold text-base mb-3 flex items-center gap-2">
                            Cambiar estatus
                            <HelpTip text="Mueve el servicio al siguiente paso del flujo. Cada cambio queda registrado con tu nombre, hora y comentario en la bitácora." />
                        </h3>
                        <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} className="fm-input mb-2" data-testid="new-status-select" title="Selecciona el nuevo estatus. Normalmente la tripulación lo cambia desde CrewMobile, pero desde aquí también puedes hacerlo manualmente.">
                            <option value="">Seleccionar...</option>
                            {Object.entries(STATUS_LABELS).map(([k, v]) => (
                                <option key={k} value={k}>{v}</option>
                            ))}
                        </select>
                        <input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Comentario opcional" className="fm-input mb-2" title="Comentario opcional que se guardará en la bitácora del servicio." />
                        <button onClick={updateStatus} disabled={!newStatus} className="fm-btn-accent w-full justify-center" data-testid="update-status-btn" title="Aplica el cambio de estatus al servicio.">
                            Actualizar estatus
                        </button>
                    </div>

                    {/* FRAP & Invoice actions */}
                    <div className="fm-card p-5">
                        <h3 className="font-display font-bold text-base mb-3 flex items-center gap-2">
                            Acciones
                            <HelpTip text="Acciones rápidas sobre este servicio: enviar pre-aviso al hospital, generar PDFs (reporte / FRAP / factura), enviar encuesta NPS y facturar." />
                        </h3>
                        <div className="space-y-2">
                            <button
                                onClick={() => setPrealertOpen(true)}
                                className="bg-red-600 hover:bg-red-700 text-white w-full py-2.5 rounded-sm font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-2"
                                data-testid="prealert-btn"
                                title="Envía aviso por correo/SMS al hospital receptor con motivo y hora estimada de arribo. Requiere Resend o Twilio activo en Integraciones."
                            >
                                <BellRing className="w-3.5 h-3.5" /> Pre-aviso al hospital
                            </button>
                            <button
                                onClick={notifyClient}
                                disabled={notifying}
                                className="bg-green-600 hover:bg-green-700 text-white w-full py-2.5 rounded-sm font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-60"
                                data-testid="notify-client-btn"
                                title="Notifica al cliente vía WhatsApp + correo el estatus actual del servicio (folio, paciente, unidad). Requiere Twilio/Resend configurados — si no, envía en modo mock."
                            >
                                <MessageCircle className="w-3.5 h-3.5" /> {notifying ? "Enviando..." : "Notificar al cliente"}
                            </button>
                            {["servicio_finalizado", "reporte_cerrado", "facturado", "cobrado"].includes(service.status) && (
                                <button
                                    onClick={sendNPS}
                                    disabled={sendingNps}
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white w-full py-2.5 rounded-sm font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-2"
                                    data-testid="send-nps-btn"
                                    title="Manda al cliente una encuesta corta (0-10) para medir su satisfacción. Llega por correo. Lo normal es que el sistema la envíe sola al cerrar."
                                >
                                    <Heart className="w-3.5 h-3.5" /> {sendingNps ? "Enviando..." : "Enviar encuesta NPS"}
                                </button>
                            )}
                            <a href={`${API_BASE}/pdf/service/${id}`} target="_blank" rel="noreferrer" className="fm-btn-outline w-full justify-center" data-testid="download-service-pdf" title="Descarga el reporte ejecutivo del servicio en PDF (datos del paciente, tripulación, tiempos, evidencias).">
                                <FileDown className="w-3.5 h-3.5" /> Reporte PDF
                            </a>
                            {frap && (
                                <a href={`${API_BASE}/pdf/frap/${id}`} target="_blank" rel="noreferrer" className="fm-btn-outline w-full justify-center" data-testid="download-frap-pdf" title="Descarga el FRAP (formato clínico) firmado en PDF. Es el documento clínico-legal del traslado.">
                                    <Download className="w-3.5 h-3.5" /> Descargar FRAP PDF
                                </a>
                            )}
                            <Link to={`/services/${id}/frap`} className="fm-btn-outline w-full justify-center" title="Abre el FRAP digital para llenar signos vitales, procedimientos, medicamentos y firmas.">
                                <FileText className="w-3.5 h-3.5" /> {frap ? "Ver/Editar FRAP" : "Crear FRAP"}
                            </Link>
                            {!service.invoiced && client && (
                                <button onClick={createInvoice} className="fm-btn-primary w-full justify-center" title="Genera la factura proforma del servicio (luego se puede timbrar con Ecofactura para CFDI 4.0).">
                                    Generar factura
                                </button>
                            )}
                            {service.invoiced && service.invoice_id && (
                                <a href={`${API_BASE}/pdf/invoice/${service.invoice_id}`} target="_blank" rel="noreferrer" className="fm-btn-primary w-full justify-center">
                                    <Download className="w-3.5 h-3.5" /> Descargar Factura
                                </a>
                            )}
                            {service.invoiced && (
                                <div className="fm-badge fm-badge-positive w-full justify-center py-1.5">Facturado</div>
                            )}
                            {service.paid && (
                                <div className="fm-badge fm-badge-positive w-full justify-center py-1.5">Pagado</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <PreAlertModal
                service={service}
                open={prealertOpen}
                onClose={() => setPrealertOpen(false)}
                onSent={load}
            />

            {/* Modal editar logística (destino, marcar sin traslado) */}
            {editLogistics && (
                <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4" data-testid="edit-logistics-modal">
                    <div className="bg-white rounded-sm max-w-lg w-full max-h-[92vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-5 border-b border-slate-200">
                            <h2 className="font-display font-bold text-lg">Editar logística del servicio</h2>
                            <button onClick={() => setEditLogistics(false)}><FileText className="w-5 h-5 rotate-45" /></button>
                        </div>
                        <div className="p-5 space-y-3">
                            <div>
                                <label className="fm-label">Origen</label>
                                <input value={logForm.origin_address || ""} onChange={(e) => setLogForm({ ...logForm, origin_address: e.target.value })} className="fm-input" data-testid="edit-origin" autoComplete="off" />
                            </div>
                            <div>
                                <label className="fm-label">Destino (hospital o domicilio)</label>
                                <input value={logForm.destination_address || ""} onChange={(e) => setLogForm({ ...logForm, destination_address: e.target.value })} className="fm-input" data-testid="edit-destination" autoComplete="off" placeholder="Confirma destino del paciente al llegar a escena" />
                            </div>
                            <div>
                                <label className="fm-label">Hospital destino (nombre)</label>
                                <input value={logForm.hospital_destination || ""} onChange={(e) => setLogForm({ ...logForm, hospital_destination: e.target.value })} className="fm-input" autoComplete="off" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="fm-label">Paciente</label>
                                    <input value={logForm.patient_name || ""} onChange={(e) => setLogForm({ ...logForm, patient_name: e.target.value })} className="fm-input" autoComplete="off" autoCorrect="off" spellCheck={false} />
                                </div>
                                <div>
                                    <label className="fm-label">Teléfono paciente</label>
                                    <input value={logForm.patient_phone || ""} onChange={(e) => setLogForm({ ...logForm, patient_phone: e.target.value })} className="fm-input" autoComplete="off" />
                                </div>
                            </div>
                            <div>
                                <label className="fm-label">Motivo / padecimiento</label>
                                <textarea rows={2} value={logForm.reason || ""} onChange={(e) => setLogForm({ ...logForm, reason: e.target.value })} className="fm-input" />
                            </div>
                            <label className={`flex items-start gap-2 p-3 rounded-sm border cursor-pointer ${logForm.no_transport ? "bg-amber-50 border-amber-400" : "border-slate-200"}`}>
                                <input type="checkbox" checked={!!logForm.no_transport} onChange={(e) => setLogForm({ ...logForm, no_transport: e.target.checked })} className="mt-0.5" data-testid="edit-no-transport" />
                                <div className="text-xs">
                                    <div className="font-bold">🩹 Atención en el lugar — sin traslado</div>
                                    <div className="text-slate-500">Marca esto cuando el paciente fue atendido pero NO se trasladó (alta voluntaria, falsa alarma, atención básica resuelta en escena, etc.).</div>
                                </div>
                            </label>
                            <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
                                <button onClick={() => setEditLogistics(false)} className="fm-btn-outline">Cancelar</button>
                                <button onClick={saveLogistics} className="fm-btn-accent" data-testid="save-logistics-btn">Guardar cambios</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {chargesOpen && service && (
                <ServiceChargesSheet
                    service={service}
                    onClose={() => setChargesOpen(false)}
                    onUpdated={load}
                />
            )}
        </div>
    );
}
